/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Text,
    Divider,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import FieldsetForm from '../shared/ui/FieldsetForm'
import {AbsoluteCenter} from '@chakra-ui/react'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import RegistrationFields from '../forms/registration-fields'
import Link from '@salesforce/retail-react-app/app/components/link'
import {PRIVACY_POLICY_URL} from '../../constants'

/**
 * RegisterForm is a React functional component that renders a registration form for creating a new user account.
 * It utilizes various UI components and internationalization features to provide a localized and accessible user experience.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.submitForm - A callback function to handle form submission.
 * @param {Function} [props.clickSignIn=noop] - A callback function to handle the "Log in" button click, defaults to a no-operation function.
 * @param {Object} props.form - The form object used for managing form state and validation.
 * @param {string} [props.prefix] - An optional prefix to be used in the form fields.
 * @param {boolean} [props.isFirstInputFocus=false] - A flag indicating whether the first input field should be focused initially.
 * @param {boolean} [props.isCheckout=false] - A flag indicating whether the form is being used in a checkout context.
 *
 * @returns {JSX.Element} The rendered registration form component.
 *
 * The form includes fields for user registration, a privacy policy agreement, and buttons for submitting the form or navigating to the login page.
 * It also conditionally renders additional UI elements based on the `isCheckout` flag.
 */
const RegisterForm = ({
    submitForm,
    clickSignIn = noop,
    form,
    prefix,
    isFirstInputFocus = false,
    isCheckout = false
}) => {
    const {formatMessage} = useIntl()

    return (
        <FieldsetForm
            data-testid="sf-auth-modal-form-register"
            legendText={formatMessage({
                defaultMessage: 'Create your account form',
                id: 'register_form.form.legend'
            })}
            onSubmit={form.handleSubmit(submitForm)}
            error={form.formState.errors?.global?.message}
        >
            <Stack spacing={16}>
                <RegistrationFields
                    form={form}
                    prefix={prefix ? prefix : undefined}
                    isFirstInputFocus={isFirstInputFocus}
                />
                <Stack spacing={24}>
                    <Text variant="bodyBase2">
                        <FormattedMessage
                            id="register_form.message.agree_to_policy"
                            defaultMessage="By clicking on “Create account”, you accept our <policy>privacy policy</policy>."
                            values={{
                                policy: (chunks) => (
                                    <ChakraLink
                                        as={Link}
                                        // @TODO: update by actual url
                                        to={PRIVACY_POLICY_URL}
                                    >
                                        {chunks}
                                    </ChakraLink>
                                )
                            }}
                        />
                    </Text>

                    <Button
                        type="submit"
                        onClick={() => form.clearErrors('global')}
                        isLoading={form.formState.isSubmitting}
                        variant="primary"
                    >
                        <FormattedMessage
                            defaultMessage="Create Account"
                            id="register_form.button.create_account"
                        />
                    </Button>
                    {isCheckout != true && (
                        <>
                            <Box position="relative" px={0} py={6}>
                                <Divider color="borderGray" />
                                <AbsoluteCenter bg="white" px={12}>
                                    <Text variant="descriptor" textTransform="uppercase">
                                        <FormattedMessage
                                            defaultMessage="or"
                                            id="login_form.divider.or"
                                        />
                                    </Text>
                                </AbsoluteCenter>
                            </Box>
                            <Stack spacing={8} justify="center">
                                <Text
                                    textAlign="center"
                                    variant="bodyLarge2"
                                    role="heading"
                                    aria-level="2"
                                >
                                    <FormattedMessage
                                        defaultMessage="Already have an account?"
                                        id="register_form.message.already_have_account"
                                    />
                                </Text>
                                <Button variant="secondary" onClick={clickSignIn}>
                                    <FormattedMessage
                                        defaultMessage="Log in"
                                        id="register_form.action.log_in"
                                    />
                                </Button>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Stack>
        </FieldsetForm>
    )
}

RegisterForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    prefix: PropTypes.string,
    isFirstInputFocus: PropTypes.bool,
    isCheckout: PropTypes.bool
}

export default RegisterForm
